import { Box, Paper, Stack, Text, Title } from "@mantine/core";
import { OverviewCardProps } from "./types";
import { CanvasFocusAreas } from "./CanvasFocusAreas";
import { type FocusAreasGroupData } from "@/services/apitypes";
import { useIsMobile } from "@/ui";
import { useTranslation } from "react-i18next";
import { QuestionContext } from "./QuestionContext";
import { shouldShowThemeDetailMode, themeDetailQuotes } from "./themeDetailMode";
import { FocusAreaDetailView } from "../FocusArea/FocusSection";
import { accentDividerStyle, patternedLeftAccentCardProps } from "@canucci/base-mantine/theme";
import { focusBackgroundAtIndex } from "@/utils/focusPatterns";

interface OverviewFocusedCardProps extends OverviewCardProps {
  group: FocusAreasGroupData;
  votingEnabled: boolean;
  isVotingClosed: boolean;
  isMobile?: boolean;
}

function OverviewFocusedCard(props: OverviewFocusedCardProps) {
  const {
    title,
    description,
    groupIndex,
    icon,
    externalId,
    group,
    isParticipation,
    votingEnabled,
    isVotingClosed,
    isMobile: isMobileProp,
    questionContext,
  } = props;
  const textColor = "var(--text-on-dark)";
  const isMobileFromHook = useIsMobile();
  const isMobile = isMobileProp ?? isMobileFromHook;
  const { t } = useTranslation("pages");
  const hasSubthemes = (group.subthemes?.length ?? 0) > 0;
  const showThemeDetail = shouldShowThemeDetailMode(group);
  const quotes = themeDetailQuotes(group);
  const summary = group.text.summary?.trim();
  const patternCardProps = patternedLeftAccentCardProps(focusBackgroundAtIndex(groupIndex), groupIndex, true);

  return (
    <Paper
      {...patternCardProps}
      p={isMobile ? "calc(var(--mantine-spacing-xs) + 1rem + 1px)" : "calc(var(--mantine-spacing-md) + 2rem + 1px)"}
    >
      <Stack w="100%" gap={0}>
        {questionContext ? (
          <Box mb="sm" mt={isMobile ? "xl" : 0} maw="48rem">
            <QuestionContext label={questionContext.label} number={questionContext.number} inverted />
          </Box>
        ) : null}
        <Title size="h1" c={textColor} mb="sm" mt="8" fz={isMobile ? "2xl" : "3xl"} w="100%">
          {title}
        </Title>
        <Box style={accentDividerStyle} mb="md" />
        {(!showThemeDetail || summary) && description ? (
          <Text c={textColor} lh="1.45" mb="6" w="100%" fz={isMobile ? "md" : "lg"}>
            {showThemeDetail ? summary : description}
          </Text>
        ) : null}
        <Box pt={showThemeDetail ? 0 : "24"} w="100%">
          {hasSubthemes ? (
            <CanvasFocusAreas
              externalId={externalId ?? ""}
              groupIndex={groupIndex}
              groupTitle={group.text.title ?? `Theme ${groupIndex + 1}`}
              focusAreas={group.subthemes ?? []}
              isParticipation={isParticipation}
              votingEnabled={votingEnabled}
              isVotingClosed={isVotingClosed}
            />
          ) : showThemeDetail ? (
            <FocusAreaDetailView
              content={group.text.body?.trim() ?? ""}
              quotes={quotes}
              minHeight={isMobile ? undefined : "24rem"}
              spacing="lg"
            />
          ) : (
            <Text fz="sm" fs="italic" p="md" c="var(--text-on-dark-muted)" bg="var(--surface-raised)" bdrs="md">
              {t("canvas.overview.noFocusAreasInGroup")}
            </Text>
          )}
        </Box>
        {icon}
      </Stack>
    </Paper>
  );
}

export default OverviewFocusedCard;
